import { transformToRioPosition } from 'helper/position';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBoundingBox, setBoundingBox } from 'store/slices/appSlice';

import { calculateBoundingBox, getMapPosition } from '../utils';

import type { Hub, Position, VehicleV2 } from 'API';

export const useBoundingBox = (vehicles: VehicleV2[], hubs?: Hub[], positions?: Position[]) => {
    const dispatch = useDispatch();
    const boundingBox = useSelector(getBoundingBox);

    const updateBoundingBox = useCallback(() => {
        const vehiclePositions = vehicles
            .filter(vehicle => !!vehicle.position?.value?.position)
            .flatMap(vehicle =>
                vehicle.position?.value?.position ? [transformToRioPosition(getMapPosition(vehicle))] : []
            );

        const hubPositions = hubs ? hubs.map(hub => transformToRioPosition(hub.position)) : [];
        const otherPositions = positions ? positions.map(transformToRioPosition) : [];

        dispatch(setBoundingBox(calculateBoundingBox([...vehiclePositions, ...hubPositions, ...otherPositions])));
    }, [vehicles, hubs, positions, dispatch]);

    const focusOnMissionRoute = (missionVehicle: VehicleV2, missionWaypoints: Position[]) => {
        const vehiclePosition = transformToRioPosition(getMapPosition(missionVehicle));
        const waypoints = missionWaypoints.map(_ => transformToRioPosition(_));

        dispatch(setBoundingBox(calculateBoundingBox([vehiclePosition, ...waypoints])));
    };

    const resetBoundingBox = () => {
        dispatch(setBoundingBox(undefined));
    };

    useEffect(() => {
        window.addEventListener('resize', updateBoundingBox);
        return () => {
            window.removeEventListener('resize', updateBoundingBox);
        };
    }, [updateBoundingBox]);

    useEffect(() => {
        updateBoundingBox();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicles.length, hubs?.length, positions?.length]);

    return { boundingBox, updateBoundingBox, focusOnMissionRoute, resetBoundingBox };
};
